import { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ShieldCheck,
  Loader2,
  LogOut,
  Filter,
  ChevronDown,
  Save,
  X,
  Search,
  FilePlus2,
  Mail,
  Phone,
  ExternalLink,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const STATUS_OPTIONS = [
  "submitted",
  "under_review",
  "filed",
  "escalated",
  "removed",
  "not_eligible",
];

const STATUS_COLOR = {
  submitted: "bg-white/[0.04] text-white/70 border-white/15",
  under_review: "bg-white/[0.04] text-white/70 border-white/15",
  filed: "bg-[#D4AF37]/[0.10] text-[#D4AF37] border-[#D4AF37]/30",
  escalated: "bg-[#D4AF37]/[0.14] text-[#D4AF37] border-[#D4AF37]/40",
  removed: "bg-[#10B981]/[0.12] text-[#10B981] border-[#10B981]/40",
  not_eligible: "bg-white/[0.02] text-white/50 border-white/10",
};

const REASON_PRESETS = [
  "Fake / never-visited (policy)",
  "Off-topic / no service received",
  "Conflict of interest (ex-employee)",
  "Suspected spam / duplicate account",
  "Harassment / hate speech",
  "Personal information disclosure",
  "Other (see notes)",
];

export default function AdminPage() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("cases");
  const [cases, setCases] = useState([]);
  const [audits, setAudits] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");

  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const [creating, setCreating] = useState(null); // { audit, snippet, reason, review_url }
  const [creatingSaving, setCreatingSaving] = useState(false);

  const userMap = useMemo(
    () => Object.fromEntries(users.map((u) => [u.id, u])),
    [users]
  );
  const userByEmail = useMemo(
    () =>
      Object.fromEntries(users.map((u) => [String(u.email || "").toLowerCase(), u])),
    [users]
  );

  const q = search.trim().toLowerCase();
  const filteredCases = useMemo(() => {
    return cases.filter((c) => {
      if (statusFilter !== "all" && c.status !== statusFilter) return false;
      if (!q) return true;
      const owner = userMap[c.user_id] || {};
      return (
        (c.review_snippet || "").toLowerCase().includes(q) ||
        (c.reason || "").toLowerCase().includes(q) ||
        (owner.name || "").toLowerCase().includes(q) ||
        (owner.email || "").toLowerCase().includes(q)
      );
    });
  }, [cases, statusFilter, q, userMap]);

  const filteredAudits = useMemo(() => {
    if (!q) return audits;
    return audits.filter(
      (a) =>
        (a.business_name || "").toLowerCase().includes(q) ||
        (a.email || "").toLowerCase().includes(q) ||
        (a.problem_description || "").toLowerCase().includes(q)
    );
  }, [audits, q]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [cRes, uRes, aRes] = await Promise.all([
          api.get("/admin/cases"),
          api.get("/admin/users"),
          api.get("/admin/audit-requests"),
        ]);
        if (!cancelled) {
          setCases(cRes.data || []);
          setUsers(uRes.data || []);
          setAudits(aRes.data || []);
        }
      } catch (e) {
        if (!cancelled)
          toast.error(
            e?.response?.status === 403
              ? "Admins only."
              : "Could not load admin data."
          );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (user && user.role !== "admin") {
    return <Navigate to="/dashboard" replace />;
  }

  const saveEdit = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/cases/${editing.case_id}`, {
        status: editing.status,
        note: editing.note || undefined,
      });
      setCases((prev) =>
        prev.map((c) => (c.id === editing.case_id ? { ...c, ...data } : c))
      );
      toast.success(
        `Case updated → ${editing.status.replace("_", " ")} · client emailed.`
      );
      setEditing(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update case.");
    } finally {
      setSaving(false);
    }
  };

  const createCaseFromAudit = async () => {
    if (!creating) return;
    setCreatingSaving(true);
    try {
      const owner = userByEmail[String(creating.audit.email).toLowerCase()];
      if (!owner) {
        toast.error(
          `No client account found for ${creating.audit.email}. Ask them to sign up first.`
        );
        setCreatingSaving(false);
        return;
      }
      const { data: newCase } = await api.post("/admin/cases", {
        user_id: owner.id,
        review_snippet: creating.snippet,
        reason: creating.reason,
        review_url: creating.review_url || null,
        status: "submitted",
        price_usd: 195,
      });
      setCases((prev) => [newCase, ...prev]);
      toast.success(
        `New case created for ${owner.name || owner.email} — status: submitted.`
      );
      setCreating(null);
      setTab("cases");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create case.");
    } finally {
      setCreatingSaving(false);
    }
  };

  const doLogout = () => {
    logout();
    window.location.assign("/");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gh-navy text-white grid place-items-center">
        <div className="flex items-center gap-3 text-white/60 text-sm">
          <Loader2 className="w-4 h-4 animate-spin text-[#10B981]" />
          Loading admin console…
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gh-navy text-white font-body">
      <header className="border-b border-white/5 sticky top-0 z-40 bg-[#0A0F1C]/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md bg-gradient-to-br from-[#D4AF37] to-[#a5842a] grid place-items-center shadow-lg shadow-yellow-500/20">
              <ShieldCheck className="w-4 h-4 text-[#0A0F1C]" strokeWidth={2.5} />
            </div>
            <span className="font-display text-lg font-bold tracking-tight">
              Go <span className="text-[#D4AF37]">Higher</span>
              <span className="ml-2 text-xs font-body font-normal text-white/40">
                Admin
              </span>
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <Link
              to="/dashboard"
              className="hidden sm:inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-white/10 hover:border-white/25 text-white/70 hover:text-white text-xs font-semibold transition-colors"
            >
              Client view
            </Link>
            <button
              onClick={doLogout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-white/10 hover:border-white/25 text-white/70 hover:text-white text-xs font-semibold transition-colors"
            >
              <LogOut className="w-3.5 h-3.5" />
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10 lg:py-14 overflow-x-hidden">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-semibold">
              Admin console
            </div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold mt-2 leading-tight">
              {tab === "cases" ? "Case management" : "Audit requests"}
            </h1>
            <p className="mt-2 text-white/55 text-sm max-w-xl">
              {tab === "cases"
                ? "Update a case status here — clients are automatically emailed the new status with your optional note."
                : "Convert an audit request into a live case in one click. The client's account is auto-detected by email."}
            </p>
          </div>

          {/* Tabs */}
          <div className="inline-flex rounded-full border border-white/10 bg-white/[0.03] p-1 text-xs font-semibold">
            <button
              type="button"
              onClick={() => setTab("cases")}
              data-testid="admin-tab-cases"
              className={`px-4 h-8 rounded-full transition-colors ${
                tab === "cases"
                  ? "bg-white text-[#0A0F1C]"
                  : "text-white/60 hover:text-white"
              }`}
            >
              Cases ({cases.length})
            </button>
            <button
              type="button"
              onClick={() => setTab("audits")}
              data-testid="admin-tab-audits"
              className={`px-4 h-8 rounded-full transition-colors ${
                tab === "audits"
                  ? "bg-white text-[#0A0F1C]"
                  : "text-white/60 hover:text-white"
              }`}
            >
              Audit requests ({audits.length})
            </button>
          </div>
        </div>

        {/* Search + filter row */}
        <div className="mt-8 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[240px] max-w-md">
            <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-white/35 pointer-events-none" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={
                tab === "cases"
                  ? "Search client, email, review, reason…"
                  : "Search business, email, description…"
              }
              data-testid="admin-search"
              className="w-full h-10 pl-10 pr-4 rounded-full border border-white/10 bg-white/[0.03] text-white text-sm placeholder:text-white/35 focus:border-[#10B981]/60 outline-none"
            />
          </div>
          {tab === "cases" && (
            <div className="inline-flex items-center gap-2 text-xs">
              <span className="text-white/50 inline-flex items-center gap-1.5">
                <Filter className="w-3.5 h-3.5" /> Status
              </span>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                data-testid="admin-status-filter"
                className="h-9 px-3 rounded-full border border-white/10 bg-white/[0.03] text-white text-xs font-semibold focus:border-[#10B981]/60 outline-none"
              >
                <option value="all">{`All (${cases.length})`}</option>
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {`${s.replace("_", " ")} (${
                      cases.filter((c) => c.status === s).length
                    })`}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* -------- CASES TABLE -------- */}
        {tab === "cases" && (
          <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
            {filteredCases.length === 0 ? (
              <div className="p-10 text-center text-white/50 text-sm">
                {q
                  ? `No cases match "${search}".`
                  : statusFilter !== "all"
                  ? `No cases with status "${statusFilter.replace("_", " ")}".`
                  : "No cases yet."}
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-white/[0.03] text-[10px] uppercase tracking-widest text-white/45 font-bold">
                  <tr>
                    <th className="text-left px-5 py-3">Client</th>
                    <th className="text-left px-5 py-3">Review</th>
                    <th className="text-left px-5 py-3">Reason</th>
                    <th className="text-left px-5 py-3">Status</th>
                    <th className="text-right px-5 py-3">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCases.map((c) => {
                    const owner = userMap[c.user_id];
                    return (
                      <tr
                        key={c.id}
                        data-testid={`admin-case-row-${c.id}`}
                        className="border-t border-white/5 hover:bg-white/[0.015]"
                      >
                        <td className="px-5 py-4 min-w-0">
                          <div className="font-semibold text-white truncate max-w-[180px]">
                            {owner?.name || "—"}
                          </div>
                          <div className="text-[11px] text-white/45 truncate max-w-[180px]">
                            {owner?.email || c.user_id}
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          <div className="italic text-white/75 max-w-md truncate">
                            "{c.review_snippet}"
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          <div className="text-white/65 max-w-[180px] truncate">
                            {c.reason}
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          <span
                            className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[10px] uppercase tracking-widest font-bold ${STATUS_COLOR[c.status]}`}
                          >
                            {c.status.replace("_", " ")}
                          </span>
                        </td>
                        <td className="px-5 py-4 text-right">
                          <button
                            type="button"
                            onClick={() =>
                              setEditing({
                                case_id: c.id,
                                client:
                                  owner?.name || owner?.email || "client",
                                status: c.status,
                                note: "",
                                review_snippet: c.review_snippet,
                              })
                            }
                            data-testid={`admin-update-${c.id}`}
                            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10 hover:bg-[#D4AF37]/20 text-[#D4AF37] text-[11px] font-bold transition-colors"
                          >
                            Update <ChevronDown className="w-3 h-3" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* -------- AUDIT REQUESTS TABLE -------- */}
        {tab === "audits" && (
          <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
            {filteredAudits.length === 0 ? (
              <div className="p-10 text-center text-white/50 text-sm">
                {q ? `No audit requests match "${search}".` : "No audit requests yet."}
              </div>
            ) : (
              <div className="divide-y divide-white/5">
                {filteredAudits.map((a) => {
                  const owner = userByEmail[String(a.email).toLowerCase()];
                  return (
                    <div
                      key={a.id}
                      data-testid={`admin-audit-row-${a.id}`}
                      className="p-5 flex flex-wrap items-start gap-6 hover:bg-white/[0.015]"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <div className="font-display font-bold text-white text-base">
                            {a.business_name}
                          </div>
                          <span
                            className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold ${
                              a.status === "audited"
                                ? "text-[#10B981] border-[#10B981]/30 bg-[#10B981]/[0.08]"
                                : "text-white/60 border-white/15 bg-white/[0.04]"
                            }`}
                          >
                            {a.status}
                          </span>
                          {owner ? (
                            <span className="inline-flex items-center rounded-full border border-[#10B981]/30 bg-[#10B981]/[0.06] text-[#10B981] px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold">
                              account linked
                            </span>
                          ) : (
                            <span className="inline-flex items-center rounded-full border border-white/15 bg-white/[0.03] text-white/50 px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold">
                              no account
                            </span>
                          )}
                        </div>
                        <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-white/50">
                          <a
                            href={`mailto:${a.email}`}
                            className="inline-flex items-center gap-1 hover:text-[#10B981]"
                          >
                            <Mail className="w-3 h-3" />
                            {a.email}
                          </a>
                          {a.phone && (
                            <a
                              href={`tel:${a.phone}`}
                              className="inline-flex items-center gap-1 hover:text-white"
                            >
                              <Phone className="w-3 h-3" />
                              {a.phone}
                            </a>
                          )}
                          {a.website_or_profile && (
                            <a
                              href={a.website_or_profile}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 hover:text-white max-w-[220px] truncate"
                            >
                              <ExternalLink className="w-3 h-3 shrink-0" />
                              {a.website_or_profile}
                            </a>
                          )}
                        </div>
                        <p className="mt-3 text-sm text-white/75 leading-relaxed max-w-3xl">
                          {a.problem_description}
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-1.5">
                        <button
                          type="button"
                          onClick={() =>
                            setCreating({
                              audit: a,
                              snippet: "",
                              reason: REASON_PRESETS[0],
                              review_url: "",
                            })
                          }
                          data-testid={`admin-create-case-${a.id}`}
                          disabled={!owner}
                          title={
                            owner
                              ? "Create a case linked to this client"
                              : "Client has no account yet — ask them to sign up first."
                          }
                          className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full border border-[#10B981]/40 bg-[#10B981]/10 hover:bg-[#10B981]/20 text-[#10B981] text-xs font-bold transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          <FilePlus2 className="w-3.5 h-3.5" />
                          Create case
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        <p className="mt-4 text-[11px] text-white/40">
          {tab === "cases"
            ? `${filteredCases.length} of ${cases.length} cases · ${users.length} clients`
            : `${filteredAudits.length} of ${audits.length} audit requests`}
        </p>
      </main>

      {/* ---- Update case dialog ---- */}
      {editing && (
        <div
          className="fixed inset-0 z-50 grid place-items-center bg-black/70 backdrop-blur-sm p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) setEditing(null);
          }}
          data-testid="admin-edit-dialog"
        >
          <div className="w-full max-w-lg rounded-2xl border border-white/10 bg-[#0d1424] p-6 relative">
            <button
              type="button"
              onClick={() => setEditing(null)}
              className="absolute top-4 right-4 text-white/40 hover:text-white"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-semibold">
              Update case
            </div>
            <h2 className="font-display text-xl font-bold mt-1">
              For {editing.client}
            </h2>
            <div className="mt-3 rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2 italic text-white/70 text-sm">
              "{editing.review_snippet}"
            </div>

            <label className="block mt-5">
              <div className="text-[11px] uppercase tracking-[0.14em] text-white/50 font-semibold mb-1.5">
                New status
              </div>
              <select
                value={editing.status}
                onChange={(e) =>
                  setEditing({ ...editing, status: e.target.value })
                }
                data-testid="admin-edit-status"
                className="w-full h-10 px-3 rounded-lg border border-white/10 bg-white/[0.03] text-white text-sm focus:border-[#10B981]/60 outline-none"
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {s.replace("_", " ")}
                  </option>
                ))}
              </select>
            </label>

            <label className="block mt-4">
              <div className="text-[11px] uppercase tracking-[0.14em] text-white/50 font-semibold mb-1.5">
                Note to client (optional)
              </div>
              <textarea
                rows={3}
                value={editing.note}
                onChange={(e) =>
                  setEditing({ ...editing, note: e.target.value })
                }
                placeholder="e.g. Google support ticket #4820 opened — expect a decision within 5 days."
                data-testid="admin-edit-note"
                className="w-full px-3 py-2 rounded-lg border border-white/10 bg-white/[0.03] text-white text-sm resize-none focus:border-[#10B981]/60 outline-none"
              />
            </label>

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="h-10 px-4 rounded-full border border-white/10 hover:border-white/25 text-white/70 text-sm font-semibold transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveEdit}
                disabled={saving}
                data-testid="admin-edit-save"
                className="inline-flex items-center gap-2 h-10 px-5 rounded-full bg-[#10B981] hover:bg-[#0ea774] text-white text-sm font-semibold shadow-lg shadow-emerald-500/25 transition-colors disabled:opacity-60"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    Saving…
                  </>
                ) : (
                  <>
                    <Save className="w-3.5 h-3.5" />
                    Save & notify client
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---- Create case from audit dialog ---- */}
      {creating && (
        <div
          className="fixed inset-0 z-50 grid place-items-center bg-black/70 backdrop-blur-sm p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) setCreating(null);
          }}
          data-testid="admin-create-dialog"
        >
          <div className="w-full max-w-lg rounded-2xl border border-white/10 bg-[#0d1424] p-6 relative">
            <button
              type="button"
              onClick={() => setCreating(null)}
              className="absolute top-4 right-4 text-white/40 hover:text-white"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="text-xs uppercase tracking-[0.2em] text-[#10B981] font-semibold">
              New case
            </div>
            <h2 className="font-display text-xl font-bold mt-1 leading-snug">
              From audit: {creating.audit.business_name}
            </h2>
            <div className="mt-1 text-xs text-white/45">
              Client: {creating.audit.email}
            </div>

            <label className="block mt-5">
              <div className="text-[11px] uppercase tracking-[0.14em] text-white/50 font-semibold mb-1.5">
                Review snippet <span className="text-[#D4AF37]">*</span>
              </div>
              <textarea
                rows={3}
                value={creating.snippet}
                onChange={(e) =>
                  setCreating({ ...creating, snippet: e.target.value })
                }
                placeholder='Paste the exact review text, e.g. "Terrible food, would not recommend"'
                data-testid="admin-create-snippet"
                className="w-full px-3 py-2 rounded-lg border border-white/10 bg-white/[0.03] text-white text-sm resize-none focus:border-[#10B981]/60 outline-none"
              />
            </label>

            <label className="block mt-4">
              <div className="text-[11px] uppercase tracking-[0.14em] text-white/50 font-semibold mb-1.5">
                Policy reason
              </div>
              <select
                value={creating.reason}
                onChange={(e) =>
                  setCreating({ ...creating, reason: e.target.value })
                }
                data-testid="admin-create-reason"
                className="w-full h-10 px-3 rounded-lg border border-white/10 bg-white/[0.03] text-white text-sm focus:border-[#10B981]/60 outline-none"
              >
                {REASON_PRESETS.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </label>

            <label className="block mt-4">
              <div className="text-[11px] uppercase tracking-[0.14em] text-white/50 font-semibold mb-1.5">
                Review URL (optional)
              </div>
              <input
                type="url"
                value={creating.review_url}
                onChange={(e) =>
                  setCreating({ ...creating, review_url: e.target.value })
                }
                placeholder="https://maps.google.com/…"
                data-testid="admin-create-url"
                className="w-full h-10 px-3 rounded-lg border border-white/10 bg-white/[0.03] text-white text-sm focus:border-[#10B981]/60 outline-none"
              />
            </label>

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setCreating(null)}
                className="h-10 px-4 rounded-full border border-white/10 hover:border-white/25 text-white/70 text-sm font-semibold transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={createCaseFromAudit}
                disabled={creatingSaving || !creating.snippet.trim()}
                data-testid="admin-create-save"
                className="inline-flex items-center gap-2 h-10 px-5 rounded-full bg-[#10B981] hover:bg-[#0ea774] text-white text-sm font-semibold shadow-lg shadow-emerald-500/25 transition-colors disabled:opacity-60"
              >
                {creatingSaving ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    Creating…
                  </>
                ) : (
                  <>
                    <FilePlus2 className="w-3.5 h-3.5" />
                    Create case
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
